/** Shared validation + normalization for Support page file attachments. */

export const MAX_SUPPORT_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB — log zips can be large

const ALLOWED_MIMES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  'application/pdf', 'text/plain',
  'application/zip', 'application/x-zip-compressed',
]);

const EXT_TO_MIME: Record<string, string> = {
  zip: 'application/zip',
  pdf: 'application/pdf',
  txt: 'text/plain',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

function fileExtension(name: string): string {
  return name.split('.').pop()?.toLowerCase() || '';
}

/** Infer MIME when the browser leaves file.type empty (common for .zip on Windows). */
export function inferSupportUploadMimeType(file: File): string {
  const type = file.type?.trim() || '';
  if (type && type !== 'application/octet-stream') return type;

  const ext = fileExtension(file.name);
  if (ext === 'zip') return 'application/zip';
  return EXT_TO_MIME[ext] || type || 'application/octet-stream';
}

export function isAllowedSupportUpload(file: File): boolean {
  const mime = inferSupportUploadMimeType(file);
  if (ALLOWED_MIMES.has(mime)) return true;
  // Windows often reports octet-stream for zip exports
  if (mime === 'application/octet-stream' && fileExtension(file.name) === 'zip') return true;
  return false;
}

/** Re-wrap file with a resolved MIME so multipart parts include a valid Content-Type. */
export function normalizeSupportUploadFile(file: File): File {
  let type = inferSupportUploadMimeType(file);
  if (type === 'application/octet-stream' && fileExtension(file.name) === 'zip') {
    type = 'application/zip';
  }
  if (!type || type === file.type) return file;
  return new File([file], file.name, { type, lastModified: file.lastModified });
}

export function formatSupportUploadSizeLimit(): string {
  return `${Math.round(MAX_SUPPORT_UPLOAD_BYTES / (1024 * 1024))} MB`;
}
